import { useEffect, useState, useCallback } from "react";
import { api, fmt } from "@/lib/api";
import { PlatformBadge, StatusPill } from "@/components/Brand";
import {
  PaperPlaneTilt,
  WhatsappLogo,
  FacebookLogo,
  InstagramLogo,
  Copy,
  CheckCircle,
  ArrowsCounterClockwise,
  Rocket,
  ImageSquare,
  DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PLATFORM_META = {
  instagram: {
    label: "Instagram",
    icon: InstagramLogo,
    color: "#E1306C",
    bg: "#FDE7F1",
    hint: "Copia el texto, descarga la foto y abre Instagram",
  },
  facebook: {
    label: "Facebook",
    icon: FacebookLogo,
    color: "#1877F2",
    bg: "#E3EFFE",
    hint: "Abre el diálogo de Facebook con tu copy precargado",
  },
  whatsapp: {
    label: "WhatsApp",
    icon: WhatsappLogo,
    color: "#25D366",
    bg: "#E7F8EE",
    hint: "Abre WhatsApp listo para mandarlo a un grupo o contacto",
  },
};

export default function Bandeja() {
  const [data, setData] = useState({ groups: [], total_drafts: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data: payload } = await api.get("/posts/pending-publish");
      setData(payload || { groups: [], total_drafts: 0 });
    } catch (e) {
      toast.error("No se pudo cargar la bandeja");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 30 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const markPublished = async (postId) => {
    try {
      await api.post(`/posts/${postId}/mark-published`);
      toast.success("¡Publicada! Ahora la IA medirá su rendimiento");
      load(true);
    } catch (e) {
      toast.error("No se pudo marcar como publicada");
    }
  };

  const regenerateDrafts = async (productId, productName) => {
    if (!window.confirm(`¿Regenerar borradores para "${productName}"? Tarda ~30 segundos.`)) return;
    try {
      toast.info("La IA está pensando…");
      const { data: summary } = await api.post(`/products/${productId}/auto-drafts`);
      if (summary?.created?.length > 0) {
        toast.success(`Generados: ${summary.created.join(" · ")}`);
      } else if (summary?.skipped?.length > 0) {
        toast.info("Ya hay borradores recientes. Marca como publicada o elimina los anteriores para regenerar.");
      } else {
        toast.warning(summary?.reason || "Nada que generar");
      }
      load();
    } catch (e) {
      toast.error("No se pudo regenerar");
    }
  };

  return (
    <div className="px-4 sm:px-6 md:px-10 py-8 max-w-[1400px]" data-testid="bandeja-page">
      <header className="mb-6">
        <div className="overline text-klein">Modo automático</div>
        <div className="flex items-baseline gap-3 flex-wrap mt-2">
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            Bandeja de publicación
          </h1>
          <span className="text-sm text-muted2 font-medium">
            La IA prepara, tú publicas con 1 click
          </span>
        </div>
      </header>

      <HowItWorksBanner />

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div
          className="px-4 py-2.5 bg-klein text-white text-sm font-bold tabular-nums"
          data-testid="bandeja-counter-drafts"
        >
          {data.total_drafts} borradores listos
        </div>
        <div className="px-4 py-2.5 border border-zinc-300 text-sm tabular-nums">
          {data.groups.length} producto{data.groups.length === 1 ? "" : "s"}
        </div>
        {refreshing && (
          <span className="text-[11px] text-muted2 inline-flex items-center gap-1">
            <ArrowsCounterClockwise size={11} weight="bold" className="animate-spin" />
            actualizando…
          </span>
        )}
        <button
          type="button"
          onClick={() => load()}
          disabled={refreshing}
          className="ml-auto btn-ghost text-xs disabled:opacity-50"
          data-testid="bandeja-refresh"
        >
          <ArrowsCounterClockwise size={12} weight="bold" /> Refrescar
        </button>
      </div>

      {loading ? (
        <div className="text-center text-muted2 py-16">Cargando…</div>
      ) : data.groups.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-8">
          {data.groups.map((g) => (
            <ProductGroup
              key={g.product_id}
              group={g}
              onPublished={markPublished}
              onRegenerate={() => regenerateDrafts(g.product_id, g.product_nombre)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HowItWorksBanner() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("bandeja_help_collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggle = () => {
    const v = !collapsed;
    setCollapsed(v);
    try {
      localStorage.setItem("bandeja_help_collapsed", v ? "1" : "0");
    } catch (_) {
      // ignore quota errors
    }
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggle}
        className="mb-6 px-3 py-2 text-[11px] text-muted2 hover:bg-zinc-50 border border-zinc-200 inline-flex items-center gap-2"
        data-testid="bandeja-howitworks-expand"
      >
        ❓ ¿Cómo funciona esto?
      </button>
    );
  }

  return (
    <section
      className="mb-6 border border-zinc-200 bg-gradient-to-br from-[#F3F8FF] to-white relative overflow-hidden"
      data-testid="bandeja-howitworks"
    >
      <button
        type="button"
        onClick={toggle}
        className="absolute top-2 right-2 text-muted2 hover:text-ink text-xs px-2 py-1"
        title="Ocultar ayuda"
        data-testid="bandeja-howitworks-collapse"
      >
        ✕
      </button>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-klein font-bold mb-2">
          Cómo funciona el modo automático
        </div>
        <h2 className="text-lg font-bold leading-tight mb-4">
          Tú apruebas el producto. La IA prepara todo. Tú publicas con 1 click.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Step
            n="1"
            title="Activa el switch en tu producto"
            body="En Inventario marca 'Publicar en automático' cuando subas un producto que quieras anunciar."
          />
          <Step
            n="2"
            title="La IA arma 3 borradores"
            body="En ~30 segundos genera copy + hashtags + foto óptima para Instagram, Facebook y WhatsApp. Aparecen aquí."
          />
          <Step
            n="3"
            title="Publica con 1 click"
            body="Aprieta el botón de la red social. El link incluido cuenta clicks y atribuye ventas a la publicación, automáticamente."
          />
        </div>
        <div className="mt-4 px-3 py-2 bg-white border border-klein/30 text-[11px] text-muted2 leading-relaxed">
          🤖 Después de publicarla, la IA mide su rendimiento cada hora. Si una publicación no convierte, la reescribe sola. Puedes ver toda la bitácora en <strong>Publicaciones</strong> abriendo cualquier post.
        </div>
      </div>
    </section>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-9 h-9 inline-flex items-center justify-center bg-klein text-white font-black text-lg">
        {n}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold leading-tight">{title}</div>
        <p className="text-xs text-muted2 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-zinc-300 p-12 text-center" data-testid="bandeja-empty">
      <Rocket size={48} weight="duotone" className="mx-auto text-klein/30 mb-4" />
      <h2 className="text-lg font-bold">Sin borradores pendientes</h2>
      <p className="text-sm text-muted2 mt-2 max-w-md mx-auto">
        Activa el switch <strong>&ldquo;Publicar en automático&rdquo;</strong> en un producto del inventario
        y la IA empezará a generar borradores listos para mandar. Tarda unos 30 segundos por producto.
      </p>
    </div>
  );
}

function ProductGroup({ group, onPublished, onRegenerate }) {
  return (
    <section
      className="border border-zinc-200 bg-white"
      data-testid={`bandeja-group-${group.product_id}`}
    >
      <header className="px-5 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted2 font-semibold">
            Producto
          </div>
          <h2 className="text-lg font-bold truncate">{group.product_nombre}</h2>
          <div className="text-xs text-muted2 mt-0.5">
            {group.product_categoria} · {fmt(group.product_precio || 0)}
          </div>
        </div>
        <button
          type="button"
          onClick={onRegenerate}
          className="btn-ghost text-xs"
          data-testid={`regen-${group.product_id}`}
          title="Genera de nuevo los borradores faltantes con la IA"
        >
          <ArrowsCounterClockwise size={12} weight="bold" /> Regenerar
        </button>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
        {group.posts.map((p) => (
          <DraftCard key={p.id} post={p} onPublished={() => onPublished(p.id)} />
        ))}
      </div>
    </section>
  );
}

function DraftCard({ post, onPublished }) {
  const meta = PLATFORM_META[post.plataforma] || PLATFORM_META.instagram;
  const [copying, setCopying] = useState(false);

  const copyAll = async () => {
    setCopying(true);
    try {
      const full = `${post.share_text}\n\n${post.tracked_link || ""}`.trim();
      await navigator.clipboard?.writeText(full);
      toast.success("Texto copiado · pégalo en " + meta.label);
    } catch {
      toast.error("No se pudo copiar");
    } finally {
      setCopying(false);
    }
  };

  const downloadImage = () => {
    if (!post.image_base64) return toast.warning("Sin imagen");
    const a = document.createElement("a");
    a.href = post.image_base64;
    a.download = `${(post.titulo || "post").replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}.jpg`;
    a.click();
    toast.success("Foto descargada");
  };

  const openInstagram = () => {
    copyAll();
    downloadImage();
    // Instagram doesn't allow direct web composer; the best UX is copy+download
    // and bounce the user to instagram.com/ — they paste in the app.
    window.open("https://www.instagram.com/", "_blank", "noopener");
  };

  return (
    <article className="p-5 flex flex-col" data-testid={`draft-${post.id}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <PlatformBadge platform={post.plataforma} testid={`draft-platform-${post.id}`} />
        <StatusPill
          status="configured"
          label="Borrador listo"
          size="sm"
          hint="Listo para mandar a la red social"
        />
      </div>

      {post.image_base64 ? (
        <div className="aspect-square bg-zinc-100 border border-zinc-200 mb-3 overflow-hidden relative">
          <img
            src={post.image_base64}
            alt={post.titulo || ""}
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={downloadImage}
            className="absolute top-2 right-2 px-2 py-1 bg-black/60 text-white text-[10px] uppercase tracking-wide hover:bg-black"
            data-testid={`download-img-${post.id}`}
          >
            <DownloadSimple size={11} weight="bold" className="inline mr-1" /> Foto
          </button>
        </div>
      ) : (
        <div className="aspect-square bg-zinc-50 border border-dashed border-zinc-300 mb-3 flex items-center justify-center">
          <ImageSquare size={32} className="text-zinc-300" />
        </div>
      )}

      <h3 className="font-bold text-sm leading-tight">{post.titulo}</h3>
      <p className="text-xs text-zinc-700 mt-1 line-clamp-4 whitespace-pre-line">
        {post.contenido}
      </p>
      {post.cta && (
        <div className="mt-2 text-xs font-semibold text-klein">{post.cta}</div>
      )}
      {post.hashtags?.length > 0 && (
        <div className="mt-2 text-[11px] text-muted2 break-all">
          {post.hashtags.map((h) => `#${h.replace(/^#/, "")}`).join(" ")}
        </div>
      )}

      {post.tracked_link && (
        <div className="mt-3 px-2 py-1.5 bg-zinc-50 border border-zinc-200 text-[10px] font-mono text-zinc-600 break-all">
          🔗 {post.tracked_link}
        </div>
      )}

      <div className="flex-1" />

      <div className="mt-4 space-y-2">
        {post.plataforma === "whatsapp" && (
          <a
            href={post.wa_url || "#"}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-2.5 text-white text-sm font-bold w-full"
            style={{ backgroundColor: meta.color }}
            data-testid={`open-wa-${post.id}`}
          >
            <WhatsappLogo size={16} weight="fill" /> Abrir en WhatsApp
          </a>
        )}
        {post.plataforma === "facebook" && post.fb_share_url && (
          <a
            href={post.fb_share_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-2.5 text-white text-sm font-bold w-full"
            style={{ backgroundColor: meta.color }}
            data-testid={`open-fb-${post.id}`}
          >
            <FacebookLogo size={16} weight="fill" /> Compartir en Facebook
          </a>
        )}
        {post.plataforma === "instagram" && (
          <button
            type="button"
            onClick={openInstagram}
            className="flex items-center justify-center gap-2 px-3 py-2.5 text-white text-sm font-bold w-full"
            style={{ backgroundColor: meta.color }}
            data-testid={`open-ig-${post.id}`}
            title="Copia texto, descarga foto y abre Instagram"
          >
            <InstagramLogo size={16} weight="fill" /> Copiar + abrir Instagram
          </button>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={copyAll}
            disabled={copying}
            className="btn-ghost text-xs"
            data-testid={`copy-text-${post.id}`}
          >
            <Copy size={12} weight="bold" /> Copiar texto
          </button>
          <button
            type="button"
            onClick={onPublished}
            className="px-2 py-1.5 border border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] text-xs font-semibold inline-flex items-center justify-center gap-1"
            data-testid={`mark-pub-${post.id}`}
            title="Confirma que ya lo subiste para que la IA empiece a medirlo"
          >
            <CheckCircle size={12} weight="fill" /> Ya publicado
          </button>
        </div>
        <p className="text-[10px] text-muted2 leading-snug">{meta.hint}</p>
      </div>
    </article>
  );
}
